import { CircularProgress } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { Rating } from "@mui/material";
import CustomStarIcon from "../../../components/CustomStarIcon";
import { InputComment } from "../../../components/inputs/InputComment";
import TipService from "../../../services/TipService";
import { GetEmployeeResponseDto } from "../../../services/dtos/response/UserResponseDto";
import QRLandingBase from "../QRLandingBase";

const StyledRating = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconFilled": {
    color: "#f3c433",
  },
  "& .MuiRating-iconHover": {
    color: "#f3c433",
  },
}));

interface QRLandingARProps {
  employee: GetEmployeeResponseDto;
  error: { [key: string]: string };
  rating: number | null;
  animatedRating: number;
  isAnimating: boolean;
  comment: string | null;
  setComment: (value: string | null) => void;
  setError: (error: { [key: string]: string }) => void;
  handleRatingChange: (event: React.ChangeEvent<{}>, newRating: number | null) => void;
  handleRatingChangeActive: (event: React.SyntheticEvent<Element, Event>, value: number | null) => void;
  MAX_CHARACTERS: number;
  userId: string | null;
  branchId: string | null;
}

const QRLandingAR = ({
  employee,
  error,
  rating,
  animatedRating,
  isAnimating,
  comment,
  setComment,
  setError,
  handleRatingChange,
  handleRatingChangeActive,
  MAX_CHARACTERS,
  userId,
  branchId,
}: QRLandingARProps) => {
  const service = useMemo(() => new TipService(), []);
  const navigate = useNavigate();
  const [isLoadingNoTip, setIsLoadingNoTip] = useState(false);

  const handleWithoutTipPayment = useCallback(async () => {
    setIsLoadingNoTip(true);

    if (!rating) {
      setIsLoadingNoTip(false);
      setError({
        comment: "Por favor, ingrese una valoración",
      });
      return;
    }

    await service.withoutTip(
      {
        BranchId: +branchId!,
        EmployeeId: +userId!,
        RatingMessage: comment,
        RatingValue: rating,
        CountryCode: employee?.countryIsoCode?.toUpperCase() || "AR",
      },
      () => {
        if(rating && rating >= 4 && employee?.googleLink && employee?.googleLink.length > 0){
          if(employee?.googleLink?.includes("https://")){
            window.location.replace(employee?.googleLink);
          } else {
            window.location.replace(`https://${employee?.googleLink}`);
          }
        } else {
          navigate("/confirmation", {
            state: {
              rating: rating,
              googleLink: employee?.googleLink,
              userId: userId,
              branchId: branchId,
            },
          });
        }
        setIsLoadingNoTip(false);
      },
      (errorMessage) => {
        setError({ payment: errorMessage });
        setIsLoadingNoTip(false);
      }
    );
  }, [rating, comment, branchId, userId, employee, navigate, service, setError]);

  return (
    <QRLandingBase employee={employee} error={error}>
      <div className="flex flex-col items-center justify-center">
        <StyledRating
          name="simple-controlled"
          value={rating !== null ? rating : animatedRating}
          onChange={handleRatingChange}
          onChangeActive={handleRatingChangeActive}
          size="large"
          icon={<CustomStarIcon isAnimating={isAnimating} />}
          emptyIcon={<CustomStarIcon />}
          sx={{ fontSize: "3rem" }}
        />
        {error.comment && (
          <span className="text-red-500 text-sm">{error.comment}</span>
        )}
      </div>
      <div className="h-fit w-full max-w-xs lg:max-w-xl pt-4 px-4 text-[#424242] lg:px-16">
        <InputComment
          onChange={(value: string) => {
            if (value.length <= MAX_CHARACTERS) {
              setComment(value);
            }
          }}
          value={comment}
          maxCharacters={MAX_CHARACTERS}
        />
        <p className="text-xs lg:text-sm text-center pt-2 text-[#3a3a3a] font-medium">
          Los mensajes solo serán vistos por el manager del establecimiento
        </p>
      </div>

      {error.payment && (
        <span className="text-red-500 text-sm">{error.payment}</span>
      )}

      <div className="pt-8 flex justify-center gap-4 lg:max-w-lg px-4 items-center footer sm:px-10">
        <button
          onClick={handleWithoutTipPayment}
          disabled={isLoadingNoTip}
          className="rounded-full py-1 w-40 disabled:opacity-50"
          style={{
            backgroundColor: employee.headerLandingColor ?? "#3b3b3b",
            color: employee.textLandingColor ?? "#fff",
          }}
          translate="no"
        >
          {isLoadingNoTip ? (
            <div className="flex items-center justify-center">
              <CircularProgress
                sx={{
                  color: "#fff",
                }}
                size="1.5rem"
                className="animate-spin "
              />
            </div>
          ) : (
            <span>ENVIAR</span>
          )}
        </button>
      </div>
    </QRLandingBase>
  );
};

export default QRLandingAR;

